import { ChevronRight } from 'lucide-react';
import { formatMoney } from '@/utils/currency';
import { useTranslation } from 'react-i18next';


type Props = {
  summary: any;
  openingAmount: number;
};

export default function ShiftSummaryCards({ summary, openingAmount }: Props) {
  const { t } = useTranslation();

  if (!summary) return null;

  return (
    <div className="space-y-4">
      {/* Card 1: Shift Overview */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 font-semibold text-gray-700 text-[13px] uppercase tracking-wider">
          Shift Overview
        </div>
        <div className="p-4 space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-gray-500">{t('xReport.openingCash', 'Opening Cash')}</span><span className="font-medium text-gray-900">{formatMoney(summary.opening_amount ?? openingAmount ?? 0)}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">{t('shift.invoices', 'Total Invoices')}</span><span className="font-medium text-gray-900">{summary.invoice_count}</span></div>
        </div>
      </div>

      {/* Card 2: Sales Summary */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 font-semibold text-gray-700 text-[13px] uppercase tracking-wider">
          Sales Summary
        </div>
        <div className="p-4 space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-gray-500">{t('shift.totalSales', 'Total Sales')}</span><span className="font-medium text-gray-900">{formatMoney(summary.total_sales || 0)}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">{t('shift.cashSales', 'Cash Sales')}</span><span className="font-medium text-gray-900">{formatMoney(summary.cash_sales || 0)}</span></div>
          {summary.non_cash_breakdown && Object.keys(summary.non_cash_breakdown).length > 0 && (
            <details className="group">
              <summary className="flex justify-between cursor-pointer list-none">
                <span className="text-gray-500 flex items-center gap-1">
                  <ChevronRight className="w-3.5 h-3.5 transition-transform group-open:rotate-90" />
                  Bank Sales
                </span>
                <span className="font-medium text-gray-900">{formatMoney(
                  Object.values(summary.non_cash_breakdown).reduce((a, b) => (a as number) + (b as number), 0) as number
                )}</span>
              </summary>
              <div className="mt-1.5 mb-1 pl-5 space-y-1 text-gray-600 border-l-2 border-gray-100 ml-1.5">
                {Object.entries(summary.non_cash_breakdown).map(([method, amount]) => (
                  <div key={method} className="flex justify-between text-[13px]">
                    <span>{method}</span>
                    <span>{formatMoney(amount as number)}</span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      </div>

      {/* Card 3: Collections & Returns */}
      {(((summary as any).payments_collected || 0) > 0 || ((summary as any).payments_collected_bank || 0) > 0 || (summary.cash_returns || 0) > 0 || (summary.bank_returns || 0) > 0 || ((summary as any).credit_returns || 0) > 0) && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 font-semibold text-gray-700 text-[13px] uppercase tracking-wider">
            Collections & Returns
          </div>
          <div className="p-4 space-y-2 text-sm">
            {((summary as any).payments_collected || 0) > 0 && (
              <div className="flex justify-between text-blue-700">
                <span className="text-gray-500">Payments Collected (Cash) (+)</span>
                <span className="font-medium">{formatMoney((summary as any).payments_collected || 0)}</span>
              </div>
            )}
            {((summary as any).payments_collected_bank || 0) > 0 && (
              <div className="flex justify-between text-blue-700">
                <span className="text-gray-500">Payments Collected (Bank)</span>
                <span className="font-medium">{formatMoney((summary as any).payments_collected_bank || 0)}</span>
              </div>
            )}
            {(summary.cash_returns || 0) > 0 && (
              <div className="flex justify-between text-amber-700">
                <span className="text-gray-500">{t('shift.cashReturns', 'Cash Returns')}</span>
                <span className="font-medium">{formatMoney(summary.cash_returns || 0)}</span>
              </div>
            )}
            {(summary.bank_returns || 0) > 0 && (
              <div className="flex justify-between text-amber-700">
                <span className="text-gray-500">Bank Returns</span>
                <span className="font-medium">{formatMoney(summary.bank_returns || 0)}</span>
              </div>
            )}
            {((summary as any).credit_returns || 0) > 0 && (
              <div className="flex justify-between text-purple-700">
                <span className="text-gray-500">Credit Returns</span>
                <span className="font-medium">{formatMoney((summary as any).credit_returns || 0)}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Card 4: Cash Movements */}
      {((summary.paid_in || 0) > 0 || (summary.paid_out || 0) > 0 || (summary.drops || 0) > 0 || (summary.total_drawer_expenses || 0) > 0) && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 font-semibold text-gray-700 text-[13px] uppercase tracking-wider">
            Cash Movements
          </div>
          <div className="p-4 space-y-2 text-sm">
            {(summary.paid_in || 0) > 0 && (
              <div className="flex justify-between text-emerald-700">
                <span className="text-gray-500">Paid In (+)</span>
                <span className="font-medium">{formatMoney(summary.paid_in || 0)}</span>
              </div>
            )}
            {(summary.paid_out || 0) > 0 && (
              <div className="flex justify-between text-orange-700">
                <span className="text-gray-500">Paid Out (-)</span>
                <span className="font-medium">{formatMoney(summary.paid_out || 0)}</span>
              </div>
            )}
            {(summary.drops || 0) > 0 && (
              <div className="flex justify-between text-red-600">
                <span className="text-gray-500">Drops (-)</span>
                <span className="font-medium">{formatMoney(summary.drops || 0)}</span>
              </div>
            )}
            {(summary.total_drawer_expenses || 0) > 0 && (
              <div className="flex justify-between text-red-600">
                <span className="text-gray-500">{t('shift.drawerExpenses', 'Drawer Expenses')}</span>
                <span className="font-medium">{formatMoney(summary.total_drawer_expenses || 0)}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
